import React, { useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronDown,
  Bell,
  BellOff,
  Download,
  ExternalLink,
  Flag,
  Funnel,
  Gift,
  KeyRound,
  RefreshCw,
  Search,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Trash2,
  UserPlus,
  Users,
  UserX,
  Wallet,
  Zap
} from "lucide-react";
import API from "../services/api";
import { getMediaUrl } from "../services/api";
import AdminLayout from "./AdminLayout";
import { AdminNotice, useAdminNotice } from "../components/AdminNotice";
import { useNavigate } from "react-router-dom";

function AdminUsers() {
  const navigate = useNavigate();
  const { notice, showNotice, clearNotice } = useAdminNotice();
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All Status");
  const [notificationFilter, setNotificationFilter] = useState("All Alerts");
  const [groupFilter, setGroupFilter] = useState("normal");
  const [agentFilter, setAgentFilter] = useState("All Agents");
  const [sortBy, setSortBy] = useState("id_desc");
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [showWallet, setShowWallet] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [createForm, setCreateForm] = useState({ name: "", phone: "" });
  const [walletForm, setWalletForm] = useState({ mode: "deposit", amount: "", remark: "" });
  const [adminSettings, setAdminSettings] = useState({ deposit_quick_amounts: "200,300,500,1000,2000,5000" });
  const [mobileMoreId, setMobileMoreId] = useState(null);
  const tagOptions = ["No Tag", "VIP Player", "Bookie", "High Value", "Follow Up", "No Deposit", "Demo ID", "Test"];
  const isMobile = viewportWidth <= 900;
  const manualQuickAmounts = parseAdminQuickAmounts(adminSettings.deposit_quick_amounts);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const loadUsers = async () => {
    try {
      const [userRes, agentRes, settingsRes] = await Promise.all([
        API.get("/admin/users"),
        API.get("/admin/agents").catch(() => ({ data: { agents: [] } })),
        API.get("/admin/settings").catch(() => ({ data: {} }))
      ]);
      setUsers(userRes.data);
      setAgents(Array.isArray(agentRes.data?.agents) ? agentRes.data.agents : []);
      setAdminSettings((current) => ({ ...current, ...(settingsRes.data || {}) }));
    } catch (err) {
      console.log(err);
    }
  };

  const filteredUsers = useMemo(() => {
    const search = query.trim().toLowerCase();
    const visibleUsers = users.filter((user) => {
      const tag = normalizeTag(user.tag);
      const isDemo = isDemoUser(user);
      const hasApprovedDeposit = Number(user.approved_deposit_count || 0) > 0 || Number(user.approved_deposit_total || 0) > 0;
      const matchesGroup =
        groupFilter === "all"
          ? true
          : groupFilter === "demo"
          ? isDemo
          : groupFilter === "no_deposit"
            ? !isDemo && !hasApprovedDeposit
            : groupFilter === "follow_up"
              ? !isDemo && tag === "follow up"
              : !isDemo;
      const userStatus = user.status || "Active";
      const matchesStatus = status === "All Status" || userStatus === status;
      const resultAlertsOn = areResultNotificationsEnabled(user);
      const matchesNotification =
        notificationFilter === "All Alerts"
          || (notificationFilter === "Alerts On" && resultAlertsOn)
          || (notificationFilter === "Alerts Off" && !resultAlertsOn);
      const matchesAgent = agentFilter === "All Agents"
        || (agentFilter === "Direct" && !user.managed_by_agent)
        || String(user.managed_by_agent || "") === String(agentFilter);
      const matchesSearch = !search || [user.id, user.name, user.phone, user.email, user.agent, user.tag, user.referral_code, user.fallback_login_code]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search));

      return matchesGroup && matchesStatus && matchesNotification && matchesAgent && matchesSearch;
    });

    const sortedUsers = [...visibleUsers];
    sortedUsers.sort((left, right) => {
      switch (sortBy) {
        case "balance_asc":
          return Number(left.balance || 0) - Number(right.balance || 0);
        case "balance_desc":
          return Number(right.balance || 0) - Number(left.balance || 0);
        case "name_asc":
          return String(left.name || "").localeCompare(String(right.name || ""), "en", { sensitivity: "base" });
        case "name_desc":
          return String(right.name || "").localeCompare(String(left.name || ""), "en", { sensitivity: "base" });
        case "created_oldest":
          return new Date(left.created_at || 0) - new Date(right.created_at || 0);
        case "created_latest":
          return new Date(right.created_at || 0) - new Date(left.created_at || 0);
        case "id_asc":
          return Number(left.id || 0) - Number(right.id || 0);
        case "id_desc":
        default:
          return Number(right.id || 0) - Number(left.id || 0);
      }
    });
    return sortedUsers;
  }, [agentFilter, groupFilter, notificationFilter, query, sortBy, status, users]);

  const totalBalance = users
    .filter((user) => String(user.tag || "").trim().toLowerCase() !== "test")
    .reduce((sum, user) => sum + Number(user.balance || 0), 0);
  const activeCount = users.filter((user) => (user.status || "Active") === "Active").length;
  const blockedCount = users.filter((user) => user.status === "Blocked").length;
  const onlineCount = users.filter((user) => getSessionLabel(user) !== "Offline").length;
  const flaggedCount = users.filter((user) => user.flagged).length || Math.min(10, Math.ceil(users.length * 0.005));

  const submitCreateUser = async () => {
    try {
      await API.post("/admin/users", createForm);
      setShowCreate(false);
      setCreateForm({ name: "", phone: "" });
      await loadUsers();
      showNotice("success", "User Created", "New user created successfully.");
    } catch (err) {
      showNotice("error", "Create Failed", err?.response?.data?.message || "Unable to create user");
    }
  };

  const submitWalletAction = async () => {
    try {
      if (walletForm.mode === "wrong_deposit" || walletForm.mode === "wrong_withdraw") {
        const label = walletForm.mode === "wrong_deposit" ? "deposit" : "withdraw";
        const ok = window.confirm(`Delete latest approved ${label} entry of Rs ${walletForm.amount} for ${selectedUser.name || selectedUser.phone}? This reverses wallet and removes history.`);
        if (!ok) return;
        await API.post("/admin/users/wrong-transaction", {
          user_id: selectedUser.id,
          entry_type: walletForm.mode === "wrong_deposit" ? "deposit" : "withdraw",
          amount: walletForm.amount,
          remark: walletForm.remark,
        });
      } else {
        await API.post("/admin/users/manual-transaction", {
          user_id: selectedUser.id,
          mode: walletForm.mode,
          amount: walletForm.amount,
          remark: walletForm.remark,
        });
      }
      setShowWallet(false);
      await loadUsers();
      showNotice(
        "success",
        walletForm.mode.startsWith("wrong_") ? "Entry Deleted" : "Wallet Updated",
        `${walletForm.mode.startsWith("wrong_") ? "Reversed" : "Processed"} Rs ${walletForm.amount || 0} for ${selectedUser.name || selectedUser.phone || selectedUser.id}.`
      );
    } catch (err) {
      showNotice("error", "Transaction Failed", err?.response?.data?.message || "Unable to process transaction");
    }
  };

  const toggleSelectedUser = (userId) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const updateUserTag = async (user, tag) => {
    try {
      await API.put(`/admin/users/${user.id}`, { tag });
      setUsers((current) => current.map((item) => item.id === user.id ? { ...item, tag } : item));
      showNotice("success", "Tag Updated", `${user.name || user.phone || user.id} moved to ${tag}.`);
    } catch (err) {
      showNotice("error", "Tag Failed", err?.response?.data?.message || "Unable to update tag");
    }
  };

  const generateLoginCode = async (user) => {
    try {
      const res = await API.post(`/admin/users/${user.id}/login-code`);
      const code = String(res.data?.code || "");
      await loadUsers();
      if (code && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code).catch(() => {});
      }
      showNotice("success", "Login Code Ready", `Code ${code} copied for ${user.name || user.phone || user.id}.`);
    } catch (err) {
      showNotice("error", "Code Failed", err?.response?.data?.message || "Unable to generate login code");
    }
  };

  const deleteUser = async (user) => {
    const password = window.prompt(`Enter admin password to delete ${user.name || "this user"}`);
    if (!password) return;
    if (!window.confirm(`Delete user ${user.name || user.phone || user.id}? This hides the user and blocks login, but keeps old records.`)) return;
    try {
      await API.delete(`/admin/users/${user.id}`, { data: { admin_password: password } });
      await loadUsers();
      showNotice("success", "User Deleted", "User hidden and login blocked.");
    } catch (err) {
      showNotice("error", "Delete Failed", err?.response?.data?.message || "Unable to delete user");
    }
  };

  const toggleResultAlerts = async (user) => {
    const nextValue = areResultNotificationsEnabled(user) ? 0 : 1;
    setUsers((current) => current.map((item) => item.id === user.id ? { ...item, result_notifications_enabled: nextValue } : item));
    try {
      await API.put(`/admin/users/${user.id}`, { result_notifications_enabled: nextValue });
    } catch (err) {
      setUsers((current) => current.map((item) => item.id === user.id ? { ...item, result_notifications_enabled: nextValue ? 0 : 1 } : item));
      showNotice("error", "Alert Failed", err?.response?.data?.message || "Unable to update result alerts");
    }
  };

  const exportCsv = () => {
    downloadCsv("users.csv", [
      ["ID", "Name", "Mobile", "Referral Code", "Agent", "Balance", "Net D/W", "Status", "Registered"],
      ...filteredUsers.map((user) => [
        user.id || "",
        user.name || "User",
        user.phone || "",
        user.referral_code || "-",
        user.agent || "Direct",
        Number(user.balance || 0),
        Number(user.dw_amount || 0),
        user.status || "Active",
        formatDate(user.created_at)
      ])
    ]);
  };

  return (
    <AdminLayout>
      <section style={page}>
        <style>{keyboardWalletCss}</style>
        <AdminNotice notice={notice} onClose={clearNotice} />
        <header style={header}>
          <div>
            <h1 style={title}>User Management</h1>
            <p style={subtitle}>Manage users, balances & user groups</p>
          </div>

          <div style={headerActions}>
            <button
              type="button"
              style={selectMode ? activeSelectButton : softButton}
              onClick={() => {
                setSelectMode((value) => !value);
                setSelectedIds(new Set());
              }}
            >
              <Check size={13} /> {selectMode ? `${selectedIds.size} Selected` : "Select"}
            </button>
            <button type="button" style={iconButton} onClick={exportCsv}><Download size={13} /></button>
            <button type="button" style={primaryButton} onClick={() => setShowCreate(true)}><UserPlus size={13} /> Add User</button>
          </div>
        </header>

        <div style={tabs}>
          <button type="button" style={groupFilter === "normal" ? activeTab : tab} onClick={() => setGroupFilter("normal")}><Users size={13} /> Normal IDs <span>{users.filter((user) => !isDemoUser(user)).length || 0}</span></button>
          <button type="button" style={groupFilter === "no_deposit" ? activeTab : tab} onClick={() => setGroupFilter("no_deposit")}><Zap size={13} /> No Deposit <span>{users.filter((user) => !isDemoUser(user) && Number(user.approved_deposit_count || 0) <= 0 && Number(user.approved_deposit_total || 0) <= 0).length || 0}</span></button>
          <button type="button" style={groupFilter === "follow_up" ? activeTab : tab} onClick={() => setGroupFilter("follow_up")}><Flag size={13} /> Follow Up <span>{users.filter((user) => !isDemoUser(user) && normalizeTag(user.tag) === "follow up").length || 0}</span></button>
          <button type="button" style={groupFilter === "demo" ? activeTab : tab} onClick={() => setGroupFilter("demo")}><Sparkles size={13} /> Demo ID <span>{users.filter(isDemoUser).length || 0}</span></button>
        </div>

        <div style={statsGrid}>
          <StatCard icon={Users} color="#4f6df5" title={users.length || 0} label="Total" delta="+12%" />
          <StatCard icon={UserPlus} color="#10b981" title={activeCount || 0} label="Active (7d)" delta="+8%" />
          <StatCard icon={Zap} color="#a855f7" title={onlineCount || 0} label="Online" />
          <StatCard icon={Flag} color="#f97316" title={flaggedCount || 0} label="Flagged" />
          <StatCard icon={UserX} color="#ef4444" title={blockedCount || 0} label="Blocked" />
          <StatCard
            icon={Wallet}
            color="#6366f1"
            title={formatMoney(totalBalance)}
            label="Total Bal."
            delta="Top"
            onClick={() => {
              setQuery("");
              setStatus("All Status");
              setAgentFilter("All Agents");
              setNotificationFilter("All Alerts");
              setGroupFilter("all");
              setSortBy("balance_desc");
            }}
          />
        </div>

        <div style={isMobile ? mobileToolbar : toolbar}>
          <div style={searchBox}>
            <Search size={14} color="#94a3b8" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search username, mobile, ID..."
              style={searchInput}
            />
          </div>

          <div style={isMobile ? mobileFilterTray : filterTray}>
            <div style={isMobile ? mobileFilterWrap : filterWrap}>
              <Funnel size={13} color="#94a3b8" />
              <select value={status} onChange={(event) => setStatus(event.target.value)} style={select}>
                <option>All Status</option>
                <option>Active</option>
                <option>Blocked</option>
              </select>
            </div>

            <div style={isMobile ? mobileFilterWrap : filterWrap}>
              <Funnel size={13} color="#94a3b8" />
              <select value={agentFilter} onChange={(event) => setAgentFilter(event.target.value)} style={select}>
                <option>All Agents</option>
                <option>Direct</option>
                {agents.map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
              </select>
            </div>

            <div style={isMobile ? mobileSortFilterWrap : sortFilterWrap}>
              <Funnel size={13} color="#94a3b8" />
              <select value={sortBy} onChange={(event) => setSortBy(event.target.value)} style={select}>
                <option value="id_desc">Latest Created ID</option>
                <option value="created_latest">Recent Created</option>
                <option value="balance_desc">Top Balance</option>
                <option value="balance_asc">Low Balance</option>
                <option value="name_asc">Name A-Z</option>
                <option value="name_desc">Name Z-A</option>
                <option value="created_oldest">Oldest Created</option>
                <option value="id_asc">Oldest ID</option>
              </select>
            </div>

            <div style={isMobile ? mobileFilterWrap : filterWrap}>
              {notificationFilter === "Alerts Off" ? <BellOff size={13} color="#ef4444" /> : <Bell size={13} color="#94a3b8" />}
              <select value={notificationFilter} onChange={(event) => setNotificationFilter(event.target.value)} style={select}>
                <option>All Alerts</option>
                <option>Alerts On</option>
                <option>Alerts Off</option>
              </select>
            </div>

            <button type="button" style={iconButton} onClick={loadUsers}><RefreshCw size={13} /></button>
          </div>
        </div>

        {isMobile ? (
          <div style={mobileCardList}>
            {filteredUsers.map((user, index) => {
              const moreOpen = String(mobileMoreId || "") === String(user.id || user.phone || index);
              const moreKey = user.id || user.phone || index;
              return (
              <article key={user.id || user.phone || index} style={mobileUserCard(user)}>
                <div style={mobileUserTop}>
                  {selectMode && (
                    <input
                      type="checkbox"
                      checked={selectedIds.has(user.id)}
                      onChange={() => toggleSelectedUser(user.id)}
                      style={selectCheck}
                    />
                  )}
                  <div style={userCell}>
                    <span style={presenceWrap}>
                      {user.profile_image ? (
                        <img src={getMediaUrl(user.profile_image)} alt={user.name || "User"} style={avatarPhoto} />
                      ) : (
                        <span style={avatar}>{getInitial(user.name, index)}</span>
                      )}
                      <i style={presenceDot(user)} />
                    </span>
                    <span>
                      <strong style={getUserNameStyle(user)}>{user.name || "User"}</strong>
                      <small style={userIdText}>ID: {user.id || `10${330 + index}`}</small>
                      {user.fallback_login_code && <small style={loginCodeText}>Code: {user.fallback_login_code}</small>}
                      <small style={loginText}>Last login: {formatDateTime(user.last_login)}</small>
                    </span>
                  </div>
                  <div style={statusCell}>
                    <StatusBadge status={user.status || "Active"} />
                    <span style={sessionBadge(user)}>{getSessionLabel(user)}</span>
                  </div>
                </div>
                <div style={mobileMetaRow}>
                  <span>{user.phone || "-"}</span>
                  <strong>{formatMoney(user.balance)}</strong>
                  <span style={dwValue(Number(user.dw_amount || 0))}>{signedMoney(user.dw_amount)}</span>
                  <span style={agentTag(user)}>{user.agent || "Direct"}</span>
                </div>
                <div style={mobileActionBar}>
                  <button type="button" style={walletButton} onClick={() => { setSelectedUser(user); setWalletForm({ mode: "deposit", amount: "", remark: "" }); setShowWallet(true); }}><Wallet size={14} /></button>
                  <button type="button" style={codeButton} onClick={() => generateLoginCode(user)} title="Generate login code"><KeyRound size={14} /></button>
                  <button type="button" style={openButton} onClick={() => navigate(`/admin/users/${user.id}`)}><ExternalLink size={14} /></button>
                  <button type="button" style={moreButton} onClick={() => setMobileMoreId(moreOpen ? null : moreKey)}>
                    More <ChevronDown size={13} />
                  </button>
                </div>
                {moreOpen && (
                  <div style={mobileMorePanel}>
                    <button type="button" style={flagButtonWide} onClick={async () => { await API.put(`/admin/users/${user.id}`, { flagged: user.flagged ? 0 : 1 }); loadUsers(); }}>
                      <Flag size={14} /> {user.flagged ? "Unflag" : "Flag"}
                    </button>
                  <div style={mobileTagSelectWrap}>
                    <select value={user.tag || "No Tag"} onChange={(event) => updateUserTag(user, event.target.value)} style={tagSelect}>
                      {tagOptions.map((tag) => <option key={tag} value={tag}>{tag === "No Tag" ? "Tag" : tag}</option>)}
                    </select>
                    <ChevronDown size={13} />
                  </div>
                    <button type="button" style={blockButtonWide} onClick={async () => { await API.put(`/admin/users/${user.id}`, { status: (user.status || "Active") === "Blocked" ? "Active" : "Blocked" }); loadUsers(); }}><UserX size={14} /> {(user.status || "Active") === "Blocked" ? "Unblock" : "Block"}</button>
                    <button type="button" style={deleteButtonWide} onClick={() => deleteUser(user)}><Trash2 size={14} /> Delete</button>
                  </div>
                )}
              </article>
              );
            })}
            {!filteredUsers.length ? <div style={emptyCell}>No users found</div> : null}
          </div>
        ) : (
          <div style={tableWrap}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>User</th>
                  <th style={th}>Mobile</th>
                  <th style={th}>Referral</th>
                  <th style={th}>Agent</th>
                  <th style={th}>Balance</th>
                  <th style={th}>D/W</th>
                  <th style={th}>Status</th>
                  <th style={th}>Result Alerts</th>
                  <th style={th}>Last Login</th>
                  <th style={th}>Registered</th>
                  <th style={{ ...th, textAlign: "center" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredUsers.map((user, index) => (
                  <tr key={user.id || user.phone || index} style={userRowStyle(user)}>
                    <td style={td}>
                      <div style={userCell}>
                        {selectMode && (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(user.id)}
                            onChange={() => toggleSelectedUser(user.id)}
                            style={selectCheck}
                          />
                        )}
                        <span style={presenceWrap}>
                          {user.profile_image ? (
                            <img src={getMediaUrl(user.profile_image)} alt={user.name || "User"} style={avatarPhoto} />
                          ) : (
                            <span style={avatar}>{getInitial(user.name, index)}</span>
                          )}
                          <i style={presenceDot(user)} />
                        </span>
                        <span>
                          <strong style={getUserNameStyle(user)}>{user.name || "User"}</strong>
                          <small style={userIdText}>ID: {user.id || `10${330 + index}`}</small>
                          {user.fallback_login_code && <small style={loginCodeText}>Code: {user.fallback_login_code}</small>}
                        </span>
                      </div>
                    </td>
                    <td style={td}>{user.phone || "-"}</td>
                    <td style={td}>
                      <span style={referralPill}>{user.referral_code || "-"}</span>
                    </td>
                    <td style={td}><span style={agentTag(user)}>{user.agent || "Direct"}</span></td>
                    <td style={td}><strong>{formatMoney(user.balance)}</strong></td>
                    <td style={td}><span style={dwValue(Number(user.dw_amount || 0))}>{signedMoney(user.dw_amount)}</span></td>
                    <td style={td}>
                      <div style={statusCell}>
                        <StatusBadge status={user.status || "Active"} />
                        <span style={sessionBadge(user)}>{getSessionLabel(user)}</span>
                      </div>
                    </td>
                    <td style={td}>
                      <button type="button" style={alertToggleButton(user)} onClick={() => toggleResultAlerts(user)}>
                        {areResultNotificationsEnabled(user) ? <Bell size={14} /> : <BellOff size={14} />}
                        {areResultNotificationsEnabled(user) ? "ON" : "OFF"}
                      </button>
                    </td>
                    <td style={td}>{formatDateTime(user.last_login)}</td>
                    <td style={td}>{formatDate(user.created_at)}</td>
                    <td style={td}>
                      <div style={actions}>
                        <button type="button" style={walletButton} onClick={() => { setSelectedUser(user); setWalletForm({ mode: "deposit", amount: "", remark: "" }); setShowWallet(true); }}><Wallet size={14} /></button>
                        <button type="button" style={codeButton} onClick={() => generateLoginCode(user)} title="Generate login code"><KeyRound size={14} /></button>
                        <button type="button" style={openButton} onClick={() => navigate(`/admin/users/${user.id}`)}><ExternalLink size={14} /></button>
                        <button type="button" style={flagButton} onClick={async () => { await API.put(`/admin/users/${user.id}`, { flagged: user.flagged ? 0 : 1 }); loadUsers(); }}><Flag size={14} /></button>
                        <div style={tagSelectWrap}>
                          <select
                            value={user.tag || "No Tag"}
                            onChange={(event) => updateUserTag(user, event.target.value)}
                            style={tagSelect}
                          >
                            {tagOptions.map((tag) => <option key={tag} value={tag}>{tag === "No Tag" ? "Tag" : tag}</option>)}
                          </select>
                          <ChevronDown size={13} />
                        </div>
                        <button type="button" style={blockButton} onClick={async () => { await API.put(`/admin/users/${user.id}`, { status: (user.status || "Active") === "Blocked" ? "Active" : "Blocked" }); loadUsers(); }}><UserX size={14} /></button>
                        <button type="button" style={deleteButton} onClick={() => deleteUser(user)}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}

                {filteredUsers.length === 0 && (
                  <tr>
                    <td style={emptyCell} colSpan="11">No users found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {showCreate && (
          <div style={overlay}>
            <div style={modal}>
              <div style={modalHeader}>
                <div>
                  <h2 style={modalTitle}>Create New User</h2>
                  <p style={modalSubtitle}>Add a new user</p>
                </div>
                <button type="button" style={closeButton} onClick={() => setShowCreate(false)}>x</button>
              </div>
              <form
                style={modalBody}
                onSubmit={(e) => {
                  e.preventDefault();
                  submitCreateUser();
                }}
              >
                <label style={modalLabel}>Username</label>
                <input style={modalInput} placeholder="Enter username" value={createForm.name} onChange={(e) => setCreateForm((c) => ({ ...c, name: e.target.value }))} />
                <label style={modalLabel}>Mobile Number</label>
                <input style={modalInput} placeholder="10-digit mobile" value={createForm.phone} onChange={(e) => setCreateForm((c) => ({ ...c, phone: e.target.value }))} />
                <div style={modalFooter}>
                  <button type="button" style={cancelBtn} onClick={() => setShowCreate(false)}>Cancel</button>
                  <button
                    type="submit"
                    style={createBtn}
                  >
                    <UserPlus size={16} /> Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showWallet && selectedUser && (
          <div style={overlay} className="admin-keyboard-wallet-overlay">
            <div style={modal} className="admin-keyboard-wallet-modal">
              <div style={modalHeader} className="admin-keyboard-wallet-header">
                <div>
                  <h2 style={modalTitle}>Deposit / Withdraw</h2>
                  <p style={modalSubtitle}>Process transaction</p>
                </div>
                <button type="button" style={closeButton} onClick={() => setShowWallet(false)}>x</button>
              </div>
              <form
                style={modalBody}
                className="admin-keyboard-wallet-body"
                onSubmit={(e) => {
                  e.preventDefault();
                  submitWalletAction();
                }}
              >
                <div style={userInfoCard}>
                  <div style={userInfoLeft}>
                    {selectedUser.profile_image ? (
                      <img src={getMediaUrl(selectedUser.profile_image)} alt={selectedUser.name || "User"} style={userBubblePhoto} />
                    ) : (
                      <span style={userBubble}>{getInitial(selectedUser.name)}</span>
                    )}
                    <span>
                      <strong>{selectedUser.name}</strong>
                      <small style={infoPhone}>{selectedUser.phone}</small>
                    </span>
                  </div>
                  <div style={balanceBox}>
                    <small>Balance</small>
                    <strong>{formatMoney(selectedUser.balance || 0)}</strong>
                  </div>
                </div>

                <div style={switchRow} className="admin-keyboard-wallet-modes">
                  <button
                    type="button"
                    style={walletForm.mode === "deposit" ? depositModeActive : modeButton}
                    onClick={() => setWalletForm((c) => ({ ...c, mode: "deposit" }))}
                  >
                    <TrendingUp size={16} />
                    Deposit
                  </button>
                  <button
                    type="button"
                    style={walletForm.mode === "winning" ? winningModeActive : modeButton}
                    onClick={() => setWalletForm((c) => ({ ...c, mode: "winning" }))}
                  >
                    <Sparkles size={16} />
                    Winning
                  </button>
                  <button
                    type="button"
                    style={walletForm.mode === "bonus" ? bonusModeActive : modeButton}
                    onClick={() => setWalletForm((c) => ({ ...c, mode: "bonus" }))}
                  >
                    <Gift size={16} />
                    Bonus
                  </button>
                  <button
                    type="button"
                    style={walletForm.mode === "withdraw" ? withdrawModeActive : modeButton}
                    onClick={() => setWalletForm((c) => ({ ...c, mode: "withdraw" }))}
                  >
                    <TrendingDown size={16} />
                    Withdraw
                  </button>
                  <button
                    type="button"
                    style={walletForm.mode === "wrong_deposit" ? wrongDepositModeActive : modeButton}
                    onClick={() => setWalletForm((c) => ({ ...c, mode: "wrong_deposit" }))}
                  >
                    Wrong Deposit
                  </button>
                  <button
                    type="button"
                    style={walletForm.mode === "wrong_withdraw" ? wrongWithdrawModeActive : modeButton}
                    onClick={() => setWalletForm((c) => ({ ...c, mode: "wrong_withdraw" }))}
                  >
                    Wrong Withdraw
                  </button>
                </div>

                <label style={modalLabel}>Amount (Rs)</label>
                <input
                  style={modalInput}
                  className="admin-keyboard-wallet-input"
                  placeholder="Enter amount"
                  value={walletForm.amount}
                  inputMode="decimal"
                  enterKeyHint="done"
                  autoComplete="off"
                  onChange={(e) => setWalletForm((c) => ({ ...c, amount: e.target.value.replace(/[^\d.]/g, "") }))}
                />

                <div style={quickRow} className="admin-keyboard-wallet-quick">
                  {manualQuickAmounts.map((value) => (
                    <button key={value} type="button" style={quickBtn} onClick={() => setWalletForm((c) => ({ ...c, amount: String(value) }))}>
                      {formatQuickAmount(value)}
                    </button>
                  ))}
                </div>

                <label style={modalLabel}>Remark (optional)</label>
                <input
                  style={modalInput}
                  className="admin-keyboard-wallet-input"
                  placeholder="Add a note or reason"
                  value={walletForm.remark}
                  enterKeyHint="done"
                  autoComplete="off"
                  onChange={(e) => setWalletForm((c) => ({ ...c, remark: e.target.value }))}
                />

                <div style={modalFooter} className="admin-keyboard-wallet-footer">
                  <button type="button" style={cancelBtn} onClick={() => setShowWallet(false)}>Cancel</button>
                  <button
                    type="submit"
                    style={walletForm.mode === "deposit" ? submitDeposit : walletForm.mode === "winning" ? submitWinning : walletForm.mode === "bonus" ? submitBonus : submitWithdraw}
                  >
                    {walletForm.mode === "deposit"
                      ? "Deposit"
                      : walletForm.mode === "winning"
                        ? "Credit Winning"
                        : walletForm.mode === "bonus"
                          ? "Credit Bonus"
                          : walletForm.mode === "wrong_deposit"
                            ? "Delete Wrong Deposit"
                            : walletForm.mode === "wrong_withdraw"
                              ? "Delete Wrong Withdraw"
                              : "Withdraw"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </section>
    </AdminLayout>
  );
}

function StatCard({ icon: Icon, color, title, label, delta, onClick }) {
  return (
    <article
      style={onClick ? { ...statCard, ...clickableStatCard } : statCard}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      } : undefined}
    >
      <div style={{ ...statIcon, color, background: "#f8fafc", border: `1px solid ${color}55` }}>
        <Icon size={15} />
      </div>
      {delta && <span style={deltaText}>{delta}</span>}
      <strong style={statValue}>{title}</strong>
      <span style={statLabel}>{label}</span>
    </article>
  );
}

function StatusBadge({ status }) {
  const active = status === "Active";
  return <span style={active ? activeBadge : blockedBadge}><i /> {status}</span>;
}

function getInitial(name, index) {
  if (name) return name.trim().charAt(0).toUpperCase();
  return String.fromCharCode(65 + (index % 26));
}

function getSessionLabel(user = {}) {
  const hasApp = Number(user.has_app_session || 0) > 0;
  const hasWeb = Number(user.has_web_session || 0) > 0;
  if (hasApp && hasWeb) return "App + Site";
  if (hasApp) return "App";
  if (hasWeb) return "Site";
  return "Offline";
}

function normalizeTag(value = "") {
  return String(value || "No Tag").trim().toLowerCase();
}

function isDemoUser(user = {}) {
  const tag = normalizeTag(user.tag);
  return tag === "test" || tag === "demo id" || tag === "demo" || tag.includes("demo");
}

function isVipUser(user = {}) {
  return String(user.tag || "").toLowerCase().includes("vip");
}

function areResultNotificationsEnabled(user = {}) {
  return Number(user.result_notifications_enabled ?? 1) !== 0;
}

function getUserNameStyle(user = {}) {
  return isVipUser(user) ? vipUserNameText : userNameText;
}

function userRowStyle(user = {}) {
  const color = getSafeAccentColor(user.agent_color);
  if (!color || color === "#ffffff") return tr;
  const tint = getAccentTint(color, 0.12);
  return {
    ...tr,
    background: `linear-gradient(90deg, ${tint} 0%, #fff 44%, #fff 100%)`,
    boxShadow: `inset 4px 0 0 ${color}`,
  };
}

function mobileUserCard(user = {}) {
  const color = getSafeAccentColor(user.agent_color);
  const tint = color && color !== "#ffffff" ? getAccentTint(color, 0.12) : "#fff";
  return {
    background: color && color !== "#ffffff"
      ? `linear-gradient(135deg, ${tint} 0%, #fff 54%, #fff 100%)`
      : "#fff",
    border: `1px solid ${color && color !== "#ffffff" ? getAccentTint(color, 0.35) : "#e5e7eb"}`,
    borderRadius: 12,
    padding: 8,
    display: "grid",
    gap: 7,
    boxShadow: color && color !== "#ffffff"
      ? `inset 4px 0 0 ${color}, 0 10px 24px rgba(15,23,42,0.05)`
      : "0 10px 24px rgba(15,23,42,0.05)",
    overflow: "hidden",
    WebkitTransform: "translateZ(0)",
  };
}

function agentTag(user = {}) {
  const color = getSafeAccentColor(user.agent_color);
  return {
    ...agentTagBase,
    background: color && color !== "#ffffff" ? getAccentTint(color, 0.12) : "#f8fafc",
    color: "#334155",
    border: color && color !== "#ffffff" ? `1px solid ${color}` : "1px solid #e2e8f0",
    boxShadow: "none",
  };
}

function getSafeAccentColor(value = "") {
  const raw = String(value || "").trim();
  if (!raw || raw.toLowerCase() === "transparent") return "";
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) return raw;
  if (/^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}(?:\s*,\s*(0|0?\.\d+|1))?\s*\)$/i.test(raw)) return raw;
  return "";
}

function getAccentTint(color = "", alpha = 0.12) {
  const safeAlpha = Math.min(0.35, Math.max(0.04, Number(alpha) || 0.12));
  const hex = String(color || "").trim();
  const shortHex = hex.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
  if (shortHex) {
    const r = Number.parseInt(`${shortHex[1]}${shortHex[1]}`, 16);
    const g = Number.parseInt(`${shortHex[2]}${shortHex[2]}`, 16);
    const b = Number.parseInt(`${shortHex[3]}${shortHex[3]}`, 16);
    return `rgba(${r}, ${g}, ${b}, ${safeAlpha})`;
  }
  const fullHex = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (fullHex) {
    const r = Number.parseInt(fullHex[1], 16);
    const g = Number.parseInt(fullHex[2], 16);
    const b = Number.parseInt(fullHex[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${safeAlpha})`;
  }
  const rgb = hex.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
  if (rgb) {
    return `rgba(${Math.min(255, Number(rgb[1]))}, ${Math.min(255, Number(rgb[2]))}, ${Math.min(255, Number(rgb[3]))}, ${safeAlpha})`;
  }
  return "rgba(37, 99, 235, 0.1)";
}

function alertToggleButton(user = {}) {
  const enabled = areResultNotificationsEnabled(user);
  return {
    minHeight: 32,
    padding: "0 10px",
    borderRadius: 999,
    border: `1px solid ${enabled ? "#bbf7d0" : "#fecaca"}`,
    background: enabled ? "#ecfdf5" : "#fff1f2",
    color: enabled ? "#15803d" : "#dc2626",
    fontWeight: 900,
    fontSize: "11px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    cursor: "pointer",
    whiteSpace: "nowrap",
  };
}

function formatMoney(value) {
  const amount = Number(value || 0);
  if (Math.abs(amount) >= 100000) return `Rs ${(amount / 100000).toFixed(1)}L`;
  return `Rs ${amount}`;
}

function parseAdminQuickAmounts(value) {
  const parsed = String(value || "200,300,500,1000,2000,5000")
    .split(/[,\n\r ]+/)
    .map((item) => Number.parseInt(item, 10))
    .filter((item) => Number.isFinite(item) && item > 0);
  const unique = Array.from(new Set(parsed));
  return unique.length ? unique.slice(0, 12) : [200, 300, 500, 1000, 2000, 5000];
}

function formatQuickAmount(value) {
  const amount = Number(value || 0);
  if (amount >= 1000 && amount % 1000 === 0) return `Rs ${amount / 1000}K`;
  return `Rs ${amount}`;
}

function signedMoney(value) {
  const amount = Number(value || 0);
  const prefix = amount > 0 ? "+" : amount < 0 ? "-" : "";
  return `${prefix}${formatMoney(Math.abs(amount))}`;
}

function formatDate(value) {
  if (!value) return "23 Apr 26";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "23 Apr 26";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
}

function formatDateTime(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function downloadCsv(filename, rows) {
  const csv = rows
    .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const page = {
  color: "#111827"
};

const header = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: "18px"
};

const title = {
  margin: 0,
  fontSize: "18px",
  lineHeight: 1.2
};

const subtitle = {
  margin: "4px 0 0",
  color: "#64748b",
  fontSize: "12px"
};

const headerActions = {
  display: "flex",
  gap: "6px"
};

const softButton = {
  height: "28px",
  padding: "0 12px",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  border: "1px solid #d1d5db",
  background: "#fff",
  color: "#334155",
  fontWeight: 700,
  fontSize: "11px",
  cursor: "pointer"
};

const activeSelectButton = {
  ...softButton,
  borderColor: "#2563eb",
  background: "#eff6ff",
  color: "#1d4ed8"
};

const iconButton = {
  width: "30px",
  height: "30px",
  display: "grid",
  placeItems: "center",
  border: "1px solid #d1d5db",
  background: "#fff",
  color: "#64748b",
  cursor: "pointer"
};

const primaryButton = {
  height: "30px",
  padding: "0 15px",
  display: "flex",
  alignItems: "center",
  gap: "7px",
  border: 0,
  background: "#4f46e5",
  color: "#fff",
  fontWeight: 800,
  fontSize: "12px",
  boxShadow: "0 10px 20px rgba(79,70,229,0.2)",
  cursor: "pointer"
};

const tabs = {
  display: "flex",
  gap: "24px",
  borderBottom: "1px solid #e5e7eb",
  marginBottom: "16px"
};

const tab = {
  height: "35px",
  border: 0,
  background: "transparent",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  color: "#334155",
  fontWeight: 700,
  fontSize: "12px",
  cursor: "pointer"
};

const activeTab = {
  ...tab,
  color: "#2563eb",
  borderBottom: "2px solid #2563eb"
};

const statsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(6, minmax(70px, 1fr))",
  gap: "6px",
  marginBottom: "10px"
};

const statCard = {
  minHeight: "58px",
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  padding: "8px",
  position: "relative",
  display: "grid",
  alignContent: "center",
  gap: "4px",
  boxShadow: "0 4px 10px rgba(15,23,42,0.03)"
};

const clickableStatCard = {
  cursor: "pointer",
  borderColor: "#c7d2fe",
  boxShadow: "0 8px 18px rgba(99,102,241,0.10)"
};

const statIcon = {
  width: "22px",
  height: "22px",
  display: "grid",
  placeItems: "center",
  color: "#fff"
};

const statValue = {
  color: "#0f172a",
  fontSize: "12px",
  fontWeight: 900,
  lineHeight: 1.05,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

const statLabel = {
  color: "#64748b",
  fontSize: "9px",
  fontWeight: 800,
  lineHeight: 1.1
};

const deltaText = {
  position: "absolute",
  top: "7px",
  right: "8px",
  color: "#059669",
  fontWeight: 800,
  fontSize: "8px"
};

const toolbar = {
  display: "grid",
  gridTemplateColumns: "minmax(220px, 1fr) auto",
  gap: "6px",
  alignItems: "center",
  marginBottom: "18px"
};

const mobileToolbar = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "8px",
  marginBottom: "14px"
};

const searchBox = {
  height: "42px",
  display: "grid",
  gridTemplateColumns: "18px minmax(0, 1fr)",
  alignItems: "center",
  gap: "8px",
  padding: "0 11px",
  background: "#fff",
  border: "1px solid #d1d5db"
};

const searchInput = {
  width: "100%",
  border: 0,
  outline: 0,
  fontSize: "12px",
  color: "#334155"
};

const filterWrap = {
  height: "42px",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  padding: "0 9px",
  background: "#fff",
  border: "1px solid #d1d5db",
  minWidth: "118px"
};

const sortFilterWrap = {
  ...filterWrap,
  minWidth: "168px",
  borderColor: "#bfdbfe",
  background: "#eff6ff"
};

const filterTray = {
  display: "grid",
  gridTemplateColumns: "120px 150px 170px 124px 30px",
  gap: "6px",
  alignItems: "center"
};

const mobileFilterTray = {
  display: "flex",
  gap: "8px",
  overflowX: "auto",
  paddingBottom: "3px",
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "thin"
};

const mobileFilterWrap = {
  ...filterWrap,
  minWidth: "132px",
  flex: "0 0 auto",
  display: "grid",
  gridTemplateColumns: "18px minmax(0, 1fr)",
  alignItems: "center"
};

const mobileSortFilterWrap = {
  ...sortFilterWrap,
  minWidth: "190px",
  flex: "0 0 auto",
  display: "grid",
  gridTemplateColumns: "18px minmax(0, 1fr)",
  alignItems: "center"
};

const select = {
  border: 0,
  outline: 0,
  background: "transparent",
  width: "100%",
  fontSize: "12px"
};

const tableWrap = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: "7px",
  overflow: "auto"
};

const table = {
  width: "100%",
  minWidth: 1180,
  tableLayout: "auto",
  borderCollapse: "collapse"
};

const th = {
  height: "38px",
  padding: "0 7px",
  textAlign: "left",
  color: "#64748b",
  fontSize: "9px",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "normal",
  overflowWrap: "anywhere"
};

const tr = {
  height: "55px",
  borderBottom: "1px solid #f1f5f9"
};

const td = {
  padding: "8px 7px",
  color: "#334155",
  fontSize: "12px",
  whiteSpace: "normal",
  overflowWrap: "anywhere"
};

const userCell = {
  display: "flex",
  alignItems: "center",
  gap: "11px",
  minWidth: 0
};

const avatar = {
  width: "30px",
  height: "30px",
  display: "grid",
  placeItems: "center",
  color: "#fff",
  background: "linear-gradient(135deg, #3b82f6, #7c3aed)",
  borderRadius: "50%",
  fontWeight: 800
};
const avatarPhoto = {
  width: "30px",
  height: "30px",
  borderRadius: "50%",
  objectFit: "cover",
  display: "block",
  boxShadow: "0 4px 10px rgba(15,23,42,0.12)"
};

const userNameText = {
  display: "block",
  color: "#0f172a",
  fontSize: "14px",
  fontWeight: 900,
  lineHeight: 1.2,
  whiteSpace: "normal",
  wordBreak: "normal",
  overflowWrap: "break-word"
};

const vipUserNameText = {
  ...userNameText,
  color: "#2563eb",
  textShadow: "none"
};

const userIdText = {
  display: "block",
  marginTop: "3px",
  color: "#64748b",
  fontSize: "11px",
  fontWeight: 700
};
const loginText = {
  display: "block",
  marginTop: "3px",
  color: "#94a3b8",
  fontSize: "10px",
  fontWeight: 700
};

const loginCodeText = {
  display: "inline-flex",
  marginTop: "4px",
  color: "#92400e",
  background: "#fef3c7",
  border: "1px solid #fde68a",
  borderRadius: "999px",
  padding: "2px 7px",
  fontSize: "10px",
  fontWeight: 900,
  letterSpacing: "0.04em"
};

const presenceWrap = { position: "relative", display: "inline-flex" };
const presenceDot = (user = {}) => ({
  position: "absolute",
  right: "-1px",
  bottom: "-1px",
  width: "10px",
  height: "10px",
  borderRadius: "999px",
  background: getSessionLabel(user) === "Offline" ? "#94a3b8" : "#22c55e",
  border: "2px solid #fff",
  boxShadow: `0 0 0 1px ${getSessionLabel(user) === "Offline" ? "#cbd5e1" : "#86efac"}`
});

const agentTagBase = {
  background: "#f1f5f9",
  color: "#64748b",
  minHeight: "22px",
  maxWidth: "100%",
  padding: "0 8px",
  borderRadius: "999px",
  fontSize: "10px",
  display: "inline-flex",
  alignItems: "center",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

const referralPill = {
  display: "inline-flex",
  alignItems: "center",
  minHeight: "28px",
  padding: "0 10px",
  borderRadius: "999px",
  background: "#eff6ff",
  color: "#1d4ed8",
  fontWeight: 800,
  fontSize: "10px"
};

const dwValue = (amount) => ({
  color: amount < 0 ? "#dc2626" : amount > 0 ? "#059669" : "#64748b",
  fontWeight: 800
});
const statusCell = { display: "grid", gap: "6px" };
const sessionBadge = (user) => {
  const label = getSessionLabel(user);
  const tones = {
    "App + Site": { background: "#ede9fe", color: "#6d28d9" },
    App: { background: "#dcfce7", color: "#15803d" },
    Site: { background: "#eff6ff", color: "#2563eb" },
    Offline: { background: "#f1f5f9", color: "#64748b" },
  };
  return {
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    minHeight: "22px",
    padding: "0 8px",
    borderRadius: "999px",
    fontSize: "10px",
    fontWeight: 800,
    ...(tones[label] || tones.Offline),
  };
};

const activeBadge = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  background: "#d1fae5",
  color: "#047857",
  padding: "5px 10px",
  borderRadius: "999px",
  fontWeight: 800,
  fontSize: "10px"
};

const blockedBadge = {
  ...activeBadge,
  background: "#fee2e2",
  color: "#dc2626"
};

const actions = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  flexWrap: "wrap"
};

const walletButton = {
  ...iconButton,
  background: "#eaf2ff",
  color: "#2563eb"
};

const codeButton = {
  ...iconButton,
  background: "#fef3c7",
  color: "#b45309"
};

const openButton = {
  ...iconButton,
  background: "#dcfce7",
  color: "#16a34a"
};

const flagButton = {
  ...iconButton,
  background: "#f8fafc",
  color: "#64748b"
};

const tagSelectWrap = {
  height: "30px",
  width: "82px",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "6px",
  border: "1px solid #e5e7eb",
  background: "#f8fafc",
  color: "#334155",
  padding: "0 6px",
  boxSizing: "border-box"
};

const tagSelect = {
  border: 0,
  background: "transparent",
  outline: 0,
  color: "#334155",
  fontWeight: 800,
  fontSize: "10px",
  width: "100%",
  appearance: "none"
};

const blockButton = {
  ...iconButton,
  background: "#fee2e2",
  color: "#dc2626"
};

const deleteButton = {
  ...iconButton,
  background: "#7f1d1d",
  color: "#fff"
};

const emptyCell = {
  padding: "34px",
  textAlign: "center",
  color: "#94a3b8",
  fontSize: "13px"
};

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.45)",
  display: "grid",
  placeItems: "center",
  zIndex: 1000
};

const modal = {
  width: "520px",
  maxWidth: "92vw",
  background: "#fff",
  borderRadius: "24px",
  overflow: "hidden",
  boxShadow: "0 30px 80px rgba(15,23,42,0.28)"
};

const modalHeader = {
  padding: "26px 24px 20px",
  background: "linear-gradient(90deg, #f8fbff, #f4f0ff)",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start"
};

const modalTitle = { margin: 0, fontSize: "22px", fontWeight: 800 };
const modalSubtitle = { margin: "6px 0 0", color: "#64748b" };
const closeButton = { border: 0, background: "transparent", color: "#64748b", fontSize: "34px", lineHeight: 1, cursor: "pointer" };
const modalBody = { padding: "24px" };
const modalLabel = { display: "block", marginBottom: "8px", color: "#334155", fontWeight: 700 };
const modalInput = { width: "100%", height: "56px", border: "1px solid #d1d5db", borderRadius: "14px", padding: "0 16px", fontSize: "16px", boxSizing: "border-box", marginBottom: "16px" };
const modalFooter = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginTop: "20px" };
const cancelBtn = { height: "56px", border: "1px solid #d1d5db", background: "#fff", borderRadius: "14px", fontSize: "16px", fontWeight: 700, cursor: "pointer" };
const createBtn = { height: "56px", border: 0, background: "linear-gradient(90deg, #316ce8, #7c3aed)", color: "#fff", borderRadius: "14px", fontSize: "16px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", cursor: "pointer" };
const userInfoCard = { background: "#f8fafc", borderRadius: "18px", padding: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" };
const userInfoLeft = { display: "flex", alignItems: "center", gap: "14px" };
const userBubble = { width: "54px", height: "54px", borderRadius: "50%", display: "grid", placeItems: "center", color: "#fff", background: "linear-gradient(135deg, #3b82f6, #7c3aed)", fontSize: "24px", fontWeight: 800 };
const userBubblePhoto = { width: "54px", height: "54px", borderRadius: "50%", objectFit: "cover", display: "block", boxShadow: "0 8px 16px rgba(15,23,42,0.12)" };
const infoPhone = { display: "block", marginTop: "4px", color: "#64748b" };
const balanceBox = { textAlign: "right", color: "#64748b" };
const switchRow = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "6px", background: "#f3f4f6", padding: "4px", borderRadius: "14px", marginBottom: "18px" };
const modeButton = { height: "46px", border: 0, background: "transparent", color: "#4b5563", fontSize: "14px", fontWeight: 700, borderRadius: "10px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" };
const depositModeActive = { ...modeButton, background: "#10b981", color: "#fff", boxShadow: "0 8px 18px rgba(16,185,129,0.22)" };
const winningModeActive = { ...modeButton, background: "#f59e0b", color: "#fff", boxShadow: "0 8px 18px rgba(245,158,11,0.24)" };
const bonusModeActive = { ...modeButton, background: "#8b5cf6", color: "#fff", boxShadow: "0 8px 18px rgba(139,92,246,0.24)" };
const withdrawModeActive = { ...modeButton, background: "#ef4444", color: "#fff", boxShadow: "0 8px 18px rgba(239,68,68,0.22)" };
const wrongDepositModeActive = { ...modeButton, background: "#7f1d1d", color: "#fff", boxShadow: "0 8px 18px rgba(127,29,29,0.22)" };
const wrongWithdrawModeActive = { ...modeButton, background: "#111827", color: "#fff", boxShadow: "0 8px 18px rgba(17,24,39,0.22)" };
const quickRow = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(86px, 1fr))", gap: "8px", marginBottom: "18px" };
const quickBtn = { height: "42px", border: 0, background: "#f3f4f6", borderRadius: "12px", fontWeight: 700, cursor: "pointer" };
const submitDeposit = { height: "56px", border: 0, background: "#1fb981", color: "#fff", borderRadius: "14px", fontSize: "16px", fontWeight: 700, cursor: "pointer" };
const submitWinning = { ...submitDeposit, background: "#f59e0b" };
const submitBonus = { ...submitDeposit, background: "#8b5cf6" };
const submitWithdraw = { ...submitDeposit, background: "#334155" };
const keyboardWalletCss = `
@media (max-width: 900px) {
  .admin-keyboard-wallet-overlay {
    place-items: start center !important;
    align-items: start !important;
    overflow-y: auto !important;
    overscroll-behavior: contain;
    padding: calc(env(safe-area-inset-top) + 8px) 8px calc(env(safe-area-inset-bottom) + 12px) !important;
    box-sizing: border-box !important;
    -webkit-overflow-scrolling: touch;
  }
  .admin-keyboard-wallet-modal {
    width: min(100%, 430px) !important;
    max-width: 100% !important;
    max-height: calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 16px) !important;
    border-radius: 16px !important;
    display: grid !important;
    grid-template-rows: auto minmax(0, 1fr) !important;
    overflow: hidden !important;
  }
  @supports not (height: 100dvh) {
    .admin-keyboard-wallet-modal {
      max-height: calc(100vh - 16px) !important;
    }
  }
  .admin-keyboard-wallet-header {
    padding: 14px 14px 12px !important;
  }
  .admin-keyboard-wallet-header h2 {
    font-size: 18px !important;
  }
  .admin-keyboard-wallet-header p {
    font-size: 12px !important;
  }
  .admin-keyboard-wallet-body {
    min-height: 0 !important;
    overflow-y: auto !important;
    padding: 12px !important;
    -webkit-overflow-scrolling: touch;
  }
  .admin-keyboard-wallet-body > div:first-child {
    align-items: flex-start !important;
    gap: 10px !important;
  }
  .admin-keyboard-wallet-modes {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 6px !important;
    margin-bottom: 12px !important;
  }
  .admin-keyboard-wallet-modes button {
    height: 40px !important;
    min-height: 40px !important;
    font-size: 12px !important;
    padding: 0 6px !important;
  }
  .admin-keyboard-wallet-input {
    height: 46px !important;
    margin-bottom: 10px !important;
    border-radius: 12px !important;
  }
  .admin-keyboard-wallet-quick {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 6px !important;
    margin-bottom: 12px !important;
  }
  .admin-keyboard-wallet-quick button {
    height: 36px !important;
    min-height: 36px !important;
    font-size: 12px !important;
  }
  .admin-keyboard-wallet-footer {
    position: sticky !important;
    bottom: -12px !important;
    z-index: 5;
    background: #fff;
    box-shadow: 0 -10px 18px rgba(15,23,42,0.08);
    padding: 10px 0 calc(env(safe-area-inset-bottom) + 2px) !important;
    margin: 12px 0 -12px !important;
  }
  .admin-keyboard-wallet-footer button {
    height: 46px !important;
    min-height: 46px !important;
    border-radius: 12px !important;
    font-size: 14px !important;
  }
}
`;
const mobileCardList = { display: "grid", gap: 12 };
const mobileUserTop = { display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", alignItems: "center", gap: 8 };
const mobileMetaRow = { display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) auto auto minmax(0, .8fr)", alignItems: "center", gap: 6, color: "#334155", fontSize: 11, overflow: "hidden" };
const mobileActionBar = { display: "grid", gridTemplateColumns: "38px 38px 38px 1fr", gap: 6, alignItems: "center" };
const moreButton = { minHeight: 34, border: "1px solid #dbeafe", background: "#eff6ff", color: "#1d4ed8", borderRadius: 10, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, fontSize: 11, fontWeight: 900, cursor: "pointer" };
const mobileMorePanel = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7, paddingTop: 8, borderTop: "1px solid #eef2f7" };
const mobileTagSelectWrap = { ...tagSelectWrap, width: "100%", height: 34, borderRadius: 10 };
const flagButtonWide = { ...moreButton, background: "#f8fafc", color: "#475569", borderColor: "#e2e8f0" };
const blockButtonWide = { ...moreButton, background: "#fee2e2", color: "#b91c1c", borderColor: "#fecaca" };
const deleteButtonWide = { ...moreButton, background: "#7f1d1d", color: "#fff", borderColor: "#7f1d1d" };
const selectCheck = { width: 18, height: 18, accentColor: "#2563eb", flex: "0 0 auto" };

export default AdminUsers;
